
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  reactive,
  ref,
} from "vue";
import { i18n } from "@/i18n";
import useBreakpoints from "@/services/Breakpoints";
import { useStore } from "vuex";
import { Billing, BillingParticipant } from "@/vue-core/models/Billing.model";
import {
  c_RegimenFiscal,
  c_UsoCFDI,
  RegimenFiscal,
  UsoCFDI,
} from "@eventmaster/cfdi-api";
import { showMessage } from "@/services/MessageService";

export default defineComponent({
  setup() {
    const emitter: any = inject("emitter");
    const dialog = ref(false);
    const { type } = useBreakpoints();
    const billingFormRef = ref<any>(null);
    const store = useStore();
    const billingForm: BillingParticipant = reactive({
      socialR: "",
      RFC: "",
      direction: "-",
      city: "-",
      state: "-",
      cp: "",
      fiscalR: RegimenFiscal.GeneralDeLeyPersonasMorales,
      CFDI: UsoCFDI.GastosEnGeneral,
      participantName: "",
      email: "contacto@inncosys.com",
    });
    const billingData = ref<Billing | null>(null);
    const billingIndex = ref(0);
    const getBillingData = computed<BillingParticipant>(() => {
      return store.getters["user/getBillingData"];
    });
    const participantData = computed(() => {
      return store.getters["user/participantData"];
    });
    const saveLocalField = (data) =>
      store.dispatch("user/saveLocalBillingData", data);
    const rules = reactive({
      participantName: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.pName"),
          trigger: "blur",
        },
      ],

      socialR: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.socialR"),
          trigger: "blur",
        },
      ],
      RFC: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.RFC"),
          trigger: "blur",
        },
        {
          min: 12,
          max: 13,
          message: i18n.global.tc("billing.rules.rfcLength"),
          trigger: "blur",
        },
      ],
      cp: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.cp"),
          trigger: "blur",
        },
        {
          min: 5,
          max: 5,
          message: i18n.global.tc("billing.rules.cpLength"),
          trigger: "blur",
        },
      ],
      fiscalR: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.fiscalR"),
          trigger: "change",
        },
      ],
      CFDI: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.CFDI"),
          trigger: "change",
        },
      ],
      /*
      email: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.email"),
          trigger: "blur",
        },
      ],
      direction: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.direction"),
          trigger: "blur",
        },
      ],
      city: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.city"),
          trigger: "blur",
        },
      ],
      state: [
        {
          required: true,
          message: i18n.global.tc("billing.rules.state"),
          trigger: "blur",
        },
      ],
      */
    });
    async function requestBilling() {
      await billingFormRef.value.validate(async (valid) => {
        if (valid) {
          saveLocalField(billingForm);
          const result: boolean = await store.dispatch("user/generateInvoice", {
            ...billingData.value,
            request: true,
            participantData: billingForm,
          });
          dialog.value = false;
        } else {
          showMessage({
            title: i18n.global.t("billing.formComplete"),
            message: i18n.global.t("billing.formComplete"),
            type: "error",
          });
        }
      });
    }
    const regimenFiscal: Array<{
      title: string;
      value: c_RegimenFiscal;
    }> = [
      { title: i18n.global.tc("billing.taxRegime.601"), value: "601" },
      { title: i18n.global.tc("billing.taxRegime.603"), value: "603" },
      { title: i18n.global.tc("billing.taxRegime.605"), value: "605" },
      { title: i18n.global.tc("billing.taxRegime.606"), value: "606" },
      { title: i18n.global.tc("billing.taxRegime.607"), value: "607" },
      { title: i18n.global.tc("billing.taxRegime.608"), value: "608" },
      { title: i18n.global.tc("billing.taxRegime.610"), value: "610" },
      { title: i18n.global.tc("billing.taxRegime.611"), value: "611" },
      { title: i18n.global.tc("billing.taxRegime.612"), value: "612" },
      { title: i18n.global.tc("billing.taxRegime.614"), value: "614" },
      { title: i18n.global.tc("billing.taxRegime.615"), value: "615" },
      { title: i18n.global.tc("billing.taxRegime.616"), value: "616" },
      { title: i18n.global.tc("billing.taxRegime.620"), value: "620" },
      { title: i18n.global.tc("billing.taxRegime.621"), value: "621" },
      { title: i18n.global.tc("billing.taxRegime.622"), value: "622" },
      { title: i18n.global.tc("billing.taxRegime.623"), value: "623" },
      { title: i18n.global.tc("billing.taxRegime.624"), value: "624" },
      { title: i18n.global.tc("billing.taxRegime.625"), value: "625" },
      { title: i18n.global.tc("billing.taxRegime.626"), value: "626" },
    ];
    const cfdiUse: Array<{
      title: string;
      value: c_UsoCFDI;
    }> = [
      { title: i18n.global.tc("billing.CFDI.G02"), value: "G02" },
      { title: i18n.global.tc("billing.CFDI.G03"), value: "G03" },
      { title: i18n.global.tc("billing.CFDI.D01"), value: "D01" },
      { title: i18n.global.tc("billing.CFDI.D04"), value: "D04" },
      { title: i18n.global.tc("billing.CFDI.D06"), value: "D06" },
      { title: i18n.global.tc("billing.CFDI.D07"), value: "D07" },
      { title: i18n.global.tc("billing.CFDI.D10"), value: "D10" },
      { title: i18n.global.tc("billing.CFDI.S01"), value: "S01" },
      { title: i18n.global.tc("billing.CFDI.CP01"), value: "CP01" },
      { title: i18n.global.tc("billing.CFDI.CN01"), value: "CN01" },
    ];
    onMounted(() => {
      emitter.on(
        "openDialogBilling",
        ({
          billingDataTemp,
          index,
        }: {
          billingDataTemp: Billing;
          index: number;
        }) => {
          dialog.value = true;
          billingData.value = billingDataTemp;
          billingIndex.value = index;
        }
      );
      billingForm.participantName = participantData.value.fullName;
      billingForm.email = participantData.value.email;
      if (getBillingData.value != null) {
        billingForm.socialR = getBillingData.value.socialR;
        billingForm.RFC = getBillingData.value.RFC;
        billingForm.direction = getBillingData.value.direction;
        billingForm.city = getBillingData.value.city;
        billingForm.state = getBillingData.value.state;
        billingForm.cp = getBillingData.value.cp;
        billingForm.fiscalR = getBillingData.value.fiscalR;
        billingForm.CFDI = getBillingData.value.CFDI;
        billingForm.participantName = participantData.value.fullName;
        billingForm.email = participantData.value.email;
      }
    });
    return {
      dialog,
      billingForm,
      rules,
      billingFormRef,
      requestBilling,
      regimenFiscal,
      cfdiUse,
      type,
      billingData,
    };
  },
});
